import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Zap, ArrowUpRight, FileText } from 'lucide-react';
import { services } from '../data/services';
import SEO from '../components/SEO';
import { useSound } from '../hooks/useSound';

const ServiceDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { playClick } = useSound();
  
  const service = services.find(s => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
        <Link to="/services" className="text-black underline">Back to Services</Link>
      </div>
    );
  }

  const Icon = service.icon;

  const { scrollY } = useScroll();
  const rawTop = useTransform(
    scrollY,
    [0, 400],
    [window.innerWidth < 768 ? 90 : 128, window.innerWidth < 768 ? 28 : 42]
  );
  const topPosition = useSpring(rawTop, { stiffness: 100, damping: 30, restDelta: 0.001 });

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#1A1A1A] font-sans selection:bg-black/10 transition-colors duration-500">
      <SEO
        title={`${service.title} | Nuviosites | TDMA`}
        description={service.description}
        image={service.image}
        url={`https://tdma.one/services/${service.slug}`}
      />

      {/* Sticky Navigation / Back Button - Portal to body to avoid ScrollSmoother translation */}
      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <motion.div 
          style={{ top: topPosition }}
          className="fixed left-8 md:left-10 z-[60]"
        >
          <button 
            onClick={() => { playClick(); navigate('/services'); }}
            className="group flex items-center justify-center gap-3 bg-white border border-black/10 backdrop-blur-md px-4 py-2 md:px-6 md:py-2.5 rounded-lg shadow-md hover:bg-black hover:text-white transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4 text-black group-hover:text-white transition-colors" />
            <span className="hidden md:block text-black group-hover:text-white font-medium text-xs md:text-sm transition-colors">
              Services
            </span>
          </button>
        </motion.div>,
        document.body
      )}

      {/* FULL WIDTH HERO */}
      <div className="w-full h-[60vh] md:h-[70vh] relative overflow-hidden">
        <motion.img 
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          src={service.image} 
          alt={service.title} 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Floating Icon */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="absolute bottom-12 left-6 md:left-20"
        >
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white flex items-center justify-center shadow-2xl">
            <Icon className="w-8 h-8 md:w-10 md:h-10 text-black stroke-[1.5]" />
          </div>
        </motion.div>
      </div>

      {/* EDITORIAL CONTENT SECTION */}
      <section className="px-6 sm:px-12 md:px-20 py-20 md:py-32 w-full max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-24 items-start">
          {/* Left Title Space */}
          <div className="md:col-span-5 lg:col-span-4">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-5xl sm:text-6xl md:text-5xl lg:text-6xl xl:text-6xl tracking-tighter leading-[0.9] font-medium uppercase mb-8"
            >
              {service.title.split(' ').map((word, i) => (
                <React.Fragment key={i}>
                  {word}<br />
                </React.Fragment>
              ))}
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="hidden md:block"
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-black text-white text-xs font-semibold px-8 py-4 rounded-full hover:bg-zinc-800 transition-all duration-300 shadow-xl"
              >
                REQUEST A QUOTE
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>

          {/* Right Content Space */}
          <div className="md:col-span-7 lg:col-span-8 flex flex-col gap-12">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl lg:text-4xl text-black/80 font-light leading-[1.3] tracking-tight"
            >
              {service.detailedDescription}
            </motion.p>

            <div className="space-y-16 mt-8">
              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-8 border-b border-black/10 pb-4">
                  <Zap className="w-6 h-6 text-black" />
                  <h3 className="text-xl font-bold uppercase tracking-widest italic">Core Expertise</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {service.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4"
                    >
                      <CheckCircle2 className="w-6 h-6 text-black shrink-0 mt-0.5" />
                      <span className="text-lg text-zinc-700 font-light">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Process & Deliverables Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                {/* Process */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-3 mb-8 border-b border-black/10 pb-4">
                    <ArrowUpRight className="w-6 h-6 text-black" />
                    <h3 className="text-xl font-bold uppercase tracking-widest italic">Process</h3>
                  </div>
                  <div className="space-y-4">
                    {service.process.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-4 group">
                        <span className="w-8 h-8 rounded-full border border-black/20 flex items-center justify-center text-xs font-bold group-hover:bg-black group-hover:text-white transition-colors">
                          {idx + 1}
                        </span>
                        <span className="text-zinc-600 font-medium">{step}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Deliverables */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-3 mb-8 border-b border-black/10 pb-4">
                    <FileText className="w-6 h-6 text-black" />
                    <h3 className="text-xl font-bold uppercase tracking-widest italic">Deliverables</h3>
                  </div>
                  <div className="space-y-4">
                    {service.deliverables.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-black shrink-0" />
                        <span className="text-zinc-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA SECTION */}
      <section className="bg-black text-white py-32 md:py-48 px-6 text-center rounded-t-[3rem] md:rounded-t-[10rem]">
        <div className="max-w-4xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-4xl md:text-7xl font-medium tracking-tighter mb-12"
          >
            Ready to elevate your business with {service.title}?
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-4 bg-white text-black px-12 py-6 rounded-full text-xl font-bold hover:bg-zinc-200 transition-all hover:gap-6"
            >
              LET'S TALK
              <ArrowUpRight className="h-6 w-6" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetails;
